import { h, safeUrl, type Child } from "../shared/safe";
import { pill } from "../shared/blocks";
import type { Ctx, Notice } from "./context";
import type { LinkView } from "./model";

// Small shared pieces for the Client Detail sections. Everything is built with h(): text is text, links are validated.

/** http(s) links open in a new tab without giving the opened page access to this one; anything else is plain text. */
export function webLink(link: LinkView | null, emptyText = "—"): Child {
  if (link === null) return emptyText;
  if (link.href === null) return h("span", { title: "Not a web address, so it is shown as text" }, link.text);
  return h("a", { href: safeUrl(link.href), target: "_blank", rel: "noopener noreferrer" }, link.text);
}

export function section(id: string, title: string, ...children: Child[]): HTMLElement {
  return h("section", { class: "card qf-cd-card", id, "aria-labelledby": `${id}-h` }, h("h2", { id: `${id}-h` }, title), ...children);
}

export function note(text: Child, extra = ""): HTMLElement {
  return h("p", { class: `muted ${extra}`.trim() }, text);
}

const TONE_CLASS: Record<Notice["tone"], string> = { ok: "qf-cd-notice--ok", warn: "qf-cd-notice--warn", bad: "qf-cd-notice--bad", info: "qf-cd-notice--info" };

/** A result message. `bad` and `warn` are announced immediately (role=alert); the rest politely (role=status). */
export function noticeBlock(n: Notice | null | undefined): HTMLElement | null {
  if (!n) return null;
  return h(
    "div",
    { class: `qf-cd-notice ${TONE_CLASS[n.tone]}`, role: n.tone === "bad" || n.tone === "warn" ? "alert" : "status" },
    h("div", null, n.text),
    n.detail && n.detail.length ? h("ul", { class: "qf-cd-notice__list" }, ...n.detail.map((d) => h("li", null, d))) : null,
    n.link ? h("a", { href: safeUrl(n.link.href), target: "_blank", rel: "noopener noreferrer" }, n.link.text) : null,
  );
}

export interface ActionButtonOptions {
  ctx: Ctx;
  /** Unique per action and target: it is both the duplicate-click guard and the focus key. */
  key: string;
  label: string;
  busyLabel: string;
  onClick: () => Promise<void> | void;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  /** Disabled for a reason other than being busy. Give `why` so it is explained, not silent. */
  disabled?: boolean;
  why?: string;
  ariaLabel?: string;
  describedBy?: string;
  /** When the same action appears twice on the page (preview and list) each copy needs its own focus key. */
  focusKey?: string;
  type?: "button" | "submit";
}

/**
 * A button that cannot be double-submitted: while its key is pending it keeps focus (aria-disabled, never `disabled`)
 * and ignores activation. The page repaints on every state change, so the busy state lives in the view state rather
 * than on the node.
 */
export function actionButton(o: ActionButtonOptions): HTMLButtonElement {
  const busy = o.ctx.state.pending.has(o.key);
  const blocked = busy || o.disabled === true;
  return h(
    "button",
    {
      type: o.type ?? "button",
      class: [o.variant && o.variant !== "primary" ? o.variant : "", busy ? "is-pending" : ""].filter(Boolean).join(" ") || false,
      "aria-busy": busy ? "true" : false,
      "aria-disabled": blocked ? "true" : false,
      "aria-label": o.ariaLabel,
      "aria-describedby": o.describedBy,
      title: o.disabled && o.why ? o.why : false,
      dataset: { focus: o.focusKey ?? o.key },
      on: {
        click: (ev) => {
          if (blocked || o.ctx.state.pending.has(o.key)) {
            ev.preventDefault();
            return;
          }
          void o.onClick();
        },
      },
    },
    busy ? o.busyLabel : o.label,
  );
}

export function statusPill(text: string, tone: "ok" | "warn" | "bad" | "neutral"): HTMLElement {
  return pill(text, tone);
}

export function lines(...items: (string | null | undefined | false)[]): string[] {
  return items.filter((i): i is string => typeof i === "string" && i !== "");
}
